import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Loader2, MessagesSquare, ShieldCheck, Clock3, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";
import { Footer } from "@/components/Footer";
import { LangToggle, useLang } from "@/lib/i18n";
import { getCommodity, FALLBACK_IMG } from "@/data/commodities";
import { scrollToId } from "@/lib/scroll";
import api from "@/lib/api";

const inputCls =
  "bg-midnight border-[#1E293B] text-[#F4F6F8] placeholder:text-[#94A3B8]/50 focus-visible:ring-gold/60 h-12 rounded-sm";

const initialForm = {
  full_name: "", company_name: "", email: "", phone: "", country: "",
  quantity: "", destination: "", delivery_terms: "", additional_info: "",
};

export default function CommodityPage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const commodity = getCommodity(slug);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (commodity) {
      const prev = document.title;
      document.title = `${commodity.name.en} Supplier & Sourcing | Norex International Trading Ltd.`;
      const meta = document.querySelector('meta[name="description"]');
      const prevDesc = meta?.getAttribute("content");
      const short = commodity.desc.en.length > 140 ? commodity.desc.en.slice(0, 137).replace(/\s+\S*$/, "") + "..." : commodity.desc.en;
      if (meta) meta.setAttribute("content", `${commodity.name.en} sourcing, supply and trade — ${short}`);
      return () => {
        document.title = prev;
        if (meta && prevDesc) meta.setAttribute("content", prevDesc);
      };
    }
  }, [slug]);

  if (!commodity) {
    return (
      <div className="min-h-screen bg-midnight flex flex-col items-center justify-center gap-6" data-testid="commodity-not-found">
        <p className="text-[#94A3B8]">{t.commodity.notFound}</p>
        <Link to="/" className="text-gold text-sm inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> NOREX
        </Link>
      </div>
    );
  }

  const industryName = t.industries.list[commodity.industryIdx]?.name || "";
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const backToIndustries = () => {
    navigate("/");
    setTimeout(() => scrollToId("industries"), 450);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("product_required", commodity.name.en);
      data.append("specifications", "");
      await api.post("/rfq", data);
      toast.success(t.rfq.success);
      setForm(initialForm);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.rfq.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="commodity-page" className="min-h-screen bg-midnight">
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#060A14]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <button onClick={backToIndustries} data-testid="commodity-back-link" className="flex items-center gap-3 group">
            <span className="w-8 h-8 bg-gold flex items-center justify-center font-display font-black text-midnight text-lg transition-transform duration-300 group-hover:rotate-90">N</span>
            <span className="inline-flex items-center gap-2 text-sm text-[#94A3B8] group-hover:text-[#F4F6F8] transition-colors duration-200">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t.commodity.back}</span>
            </span>
          </button>
          <LangToggle compact />
        </div>
      </header>

      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <img
          src={commodity.img}
          alt={commodity.name.en}
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#060A14] to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pb-16 pt-40">
          <p data-testid="commodity-category" className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-4 flex items-center gap-3">
            <span className="w-10 h-px bg-gold inline-block" /> {industryName}
          </p>
          <h1 data-testid="commodity-title" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-[#F4F6F8]" style={{ textShadow: "0 2px 30px rgba(0,0,0,0.5)" }}>
            {commodity.name[lang] || commodity.name.en}
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{t.commodity.overview}</p>
              <p data-testid="commodity-description" className="text-base md:text-lg text-[#C6D0DD] leading-relaxed">
                {commodity.desc[lang] || commodity.desc.en}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-12">
                <p className="text-xs uppercase tracking-[0.25em] text-gold mb-6">{t.commodity.specs}</p>
                <ul className="divide-y divide-[#1E293B] border-y border-[#1E293B]" data-testid="commodity-specs">
                  {commodity.specs.map((s, i) => (
                    <li key={i} className="flex items-center gap-4 py-4 px-2 hover:bg-surface transition-colors duration-200">
                      <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" />
                      <span className="text-sm md:text-base text-[#F4F6F8]">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="bg-surface border border-[#1E293B] p-8 lg:sticky lg:top-28">
                <p className="text-xs uppercase tracking-[0.25em] text-gold mb-6 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {t.commodity.regions}
                </p>
                <div className="flex flex-wrap gap-2" data-testid="commodity-regions">
                  {commodity.regions.map((r) => (
                    <span key={r} className="text-xs uppercase tracking-wider text-[#C6D0DD] border border-white/15 px-3 py-1.5 hover:border-gold/50 transition-colors duration-200">
                      {r}
                    </span>
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-[#1E293B] space-y-3">
                  <p className="flex items-center gap-3 text-sm text-[#94A3B8]">
                    <Clock3 className="w-4 h-4 text-gold shrink-0" /> {t.rfq.resp1}
                  </p>
                  <p className="flex items-center gap-3 text-sm text-[#94A3B8]">
                    <ShieldCheck className="w-4 h-4 text-gold shrink-0" /> {t.rfq.resp2}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-surface border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{t.rfq.eyebrow}</p>
            <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F4F6F8] mb-3" data-testid="commodity-form-title">
              {t.commodity.formTitle} — {commodity.name[lang] || commodity.name.en}
            </h2>
            <p className="text-sm text-[#94A3B8] mb-10">{t.commodity.formSub}</p>
            <form data-testid="commodity-rfq-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.commodity.product}</Label>
                <div data-testid="commodity-prefilled-product" className="mt-1 flex items-center gap-3 border border-gold/40 bg-gold/5 px-4 h-12 text-sm text-gold font-semibold">
                  {commodity.name[lang] || commodity.name.en}
                </div>
              </div>
              <div>
                <Label htmlFor="cm-name" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.fullName}</Label>
                <Input id="cm-name" data-testid="commodity-rfq-name" required value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder={t.rfq.placeholders.fullName} />
              </div>
              <div>
                <Label htmlFor="cm-company" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.company}</Label>
                <Input id="cm-company" data-testid="commodity-rfq-company" required value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder={t.rfq.placeholders.company} />
              </div>
              <div>
                <Label htmlFor="cm-email" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.email}</Label>
                <Input id="cm-email" data-testid="commodity-rfq-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder={t.rfq.placeholders.email} />
              </div>
              <div>
                <Label htmlFor="cm-phone" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.phone}</Label>
                <Input id="cm-phone" data-testid="commodity-rfq-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder={t.rfq.placeholders.phone} />
              </div>
              <div>
                <Label htmlFor="cm-country" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.country}</Label>
                <Input id="cm-country" data-testid="commodity-rfq-country" required value={form.country} onChange={set("country")} className={inputCls} placeholder={t.rfq.placeholders.country} />
              </div>
              <div>
                <Label htmlFor="cm-quantity" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.quantity}</Label>
                <Input id="cm-quantity" data-testid="commodity-rfq-quantity" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder={t.rfq.placeholders.quantity} />
              </div>
              <div>
                <Label htmlFor="cm-destination" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.destination}</Label>
                <Input id="cm-destination" data-testid="commodity-rfq-destination" value={form.destination} onChange={set("destination")} className={inputCls} placeholder={t.rfq.placeholders.destination} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.terms}</Label>
                <Select value={form.delivery_terms} onValueChange={(v) => setForm({ ...form, delivery_terms: v })}>
                  <SelectTrigger data-testid="commodity-rfq-terms" className={inputCls}>
                    <SelectValue placeholder={t.rfq.placeholders.terms} />
                  </SelectTrigger>
                  <SelectContent className="bg-surface2 border-[#1E293B] text-[#F4F6F8]">
                    {t.rfq.terms.map((term, i) => (
                      <SelectItem key={term} value={term} data-testid={`commodity-rfq-term-${i + 1}`}>{term}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cm-message" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.commodity.msgLabel}</Label>
                <Textarea id="cm-message" data-testid="commodity-rfq-message" value={form.additional_info} onChange={set("additional_info")} rows={4} className={`${inputCls} h-auto`} placeholder={t.commodity.msgPlaceholder} />
              </div>
              <div className="sm:col-span-2 pt-2">
                <button
                  data-testid="commodity-rfq-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gold text-midnight font-semibold px-8 py-4 rounded-full hover:bg-gold-light hover:text-midnight transition-[background-color,transform] duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                  {loading ? t.rfq.submitting : t.rfq.submit}
                </button>
              </div>
            </form>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="bg-midnight border border-[#1E293B] p-8 lg:sticky lg:top-28">
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{t.rfq.nextTitle}</p>
              {t.rfq.nextSteps.map((step, i) => (
                <div key={i} className="flex gap-4 mb-5 last:mb-0">
                  <span className="font-display font-bold text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{step}</p>
                </div>
              ))}
              <a
                data-testid="commodity-whatsapp-link"
                href="https://wa.me/+2349068043369"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 pt-6 border-t border-[#1E293B] flex items-center gap-3 text-sm text-[#F4F6F8] hover:text-gold transition-colors duration-200"
              >
                <MessagesSquare className="w-4 h-4 text-gold shrink-0" /> {t.rfq.whatsapp}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
